import { ActivationState, Client, Frame, StompSubscription } from '@stomp/stompjs';
import produce from 'immer';

import type { IOnlineUser } from 'packages/workspace/services/models/account';
import { EventFormat, EventTypes } from 'packages/workspace/services/models/global-models';
import type { InternalChatConversation } from 'packages/workspace/services/models/internal-channel';
import { MessageTopicsConstants } from 'packages/workspace/services/store/omnichannel/chat-store';
import { InternalConversationStore } from 'packages/workspace/services/store/omnichannel/internal-conversation-store';
import useSession, { sessionStore } from 'packages/workspace/services/store/session-store';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import type { ISocketContext } from '../helpers/contexts/socket';
import { createLogger } from './logger/logger';
import { signOut, fetchAuthSession } from '@aws-amplify/auth';
import useOnlineStatus from './useOnlineStatus';
import { useLogoutLogs } from 'packages/workspace/services/fetchers/workspace-info';
import { AbilityStore } from 'packages/workspace/services/store/abilities-store';
import { useRouter } from 'next/router';
import { uiStore } from 'packages/workspace/services/store/ui-store';

const fileLabel = 'useSocket.tsx';
const logger = createLogger({
  fileLabel,
});

const useSocket = (): ISocketContext => {
  const [operatorSocketClient, SetOperatorSocketClient] = useState<Client | null>(new Client());
  const [, SetActiveSubscriptions] = useState<StompSubscription[]>([]);
  const [operatorConnectionError, SetOperatorConnectionError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [connected, setConnected] = useState<boolean>(false);
  const [currentTry, setCurrentTry] = useState<number>(0);
  const [maxAttemptConnections, _] = useState<number>(20);
  const [connectionMsg, setConnectionMsg] = useState<string>('');
  const [retrying, setReTrying] = useState<boolean>(false);
  const isOnline = useOnlineStatus();
  const { mutateAsync: mutateLogoutLogs } = useLogoutLogs();
  const router = useRouter();

  const session = useSession();
  const queryClient = useQueryClient();

  const updateActivity = useCallback(
    (message: EventFormat<IOnlineUser>) => {
      //update query cache.
      const update = (entity: IOnlineUser) => {
        if (entity.id === message.data.id) {
          switch (message.event) {
            case EventTypes.USER_JOINED: {
              return produce(entity, (draftState) => {
                draftState.display_name = message.data.display_name;
                draftState.id = message.data.id;
                draftState.online = message.data.online;
              });
            }
            case EventTypes.USER_LEFT: {
              return produce(entity, (draftState) => {
                draftState.online = false;
              });
            }
            default:
              return entity;
          }
        } else {
          return entity;
        }
      };

      queryClient.setQueriesData<IOnlineUser[]>(['onlineUsers'], (oldData) => {
        if (oldData === undefined) oldData = [] as IOnlineUser[];
        const checkUser = oldData.find((eachUser) => eachUser.id === message.data.id);
        return checkUser
          ? oldData.map(update)
          : produce(oldData, (draftMessageData) => {
              draftMessageData.push(message.data);
            });
      });
    },
    [queryClient]
  );
  useEffect(() => {
    if (!isOnline) operatorSocketClient?.deactivate();
  }, [isOnline]);

  const newOneToOneConversationRequest = useCallback(
    (message: EventFormat<InternalChatConversation>) => {
      if (message.event == EventTypes.NEW_MESSAGE) {
        const activeConnection = InternalConversationStore.checkIfAlreadyConnected(message.data.conversation_id);
        if (!activeConnection) {
          queryClient.setQueryData<IOnlineUser[]>(['onlineUsers'], (prevOnlineUsers) => {
            if (prevOnlineUsers === undefined) prevOnlineUsers = [] as IOnlineUser[];
            return prevOnlineUsers.map((entity: IOnlineUser) => {
              if (entity.display_name === message.data.display_name && !entity.conversation_id) {
                entity = {
                  ...entity,
                  conversation_id: message.data.conversation_id,
                };
              }
              return entity;
            });
          });
          InternalConversationStore.updateConnections({
            id: message.data.conversation_id,
            conversation: message.data,
            connection: operatorSocketClient?.subscribe(`/topic${MessageTopicsConstants.INTERNAL_CHAT}/${message.data.conversation_id}`, (internalOneOnOneMsg) => {
              InternalConversationStore.updateConversationMessages(JSON.parse(internalOneOnOneMsg.body), message.data.conversation_id, session?.iamPrincipal?.id);
            }),
          });
        }
      }
    },
    [operatorSocketClient, session?.iamPrincipal]
  );

  const onWebSocketConnected = useCallback(() => {
    setConnected(true);
    setLoading(false);
    SetOperatorSocketClient(operatorSocketClient);
    InternalConversationStore.setClient(operatorSocketClient);
    //subscribing to Predefined queues
    InternalConversationStore.setClient(operatorSocketClient);
    operatorSocketClient.subscribe('/user/queue/pong', (message) => {
      console.log('Received pong', JSON.stringify(message.body));
    });
  }, [operatorSocketClient]);

  useEffect(() => {
    if (!operatorSocketClient) {
      return;
    }
    if (operatorSocketClient.state !== ActivationState.ACTIVE) {
      SetActiveSubscriptions((previousSubscriptions) => {
        previousSubscriptions.forEach((each) => each.unsubscribe());
        return [];
      });
    } else if (operatorSocketClient.state === ActivationState.ACTIVE && connected) {
      SetActiveSubscriptions((previousSubscriptions) =>
        produce(previousSubscriptions, (draftSubscriptions) => {
          draftSubscriptions.push(
            operatorSocketClient.subscribe(MessageTopicsConstants.USER_PERSONAL_QUEUE, (message) => {
              newOneToOneConversationRequest(JSON.parse(message.body));
            })
          );
          draftSubscriptions.push(
            operatorSocketClient.subscribe(`${MessageTopicsConstants.CURRENT_AVAILABLE_USERS}${session.iamPrincipal.workspaceId}`, (message) => {
              updateActivity(JSON.parse(message.body) as EventFormat<IOnlineUser>);
            })
          );
          draftSubscriptions.push(
            operatorSocketClient.subscribe(`${MessageTopicsConstants.AUTO_LOGOUT}`, async (message) => {
              let data = JSON.parse(message.body);
              if (data.event === EventTypes.USER_LOGOUT) {
                uiStore.setErrorMessage(data.data);
                mutateLogoutLogs({ tag: 'useSocket_Auto_Signout' });
                await signOut();
                AbilityStore.clearRules();
                sessionStore.unsetDiscoveryParams();
                queryClient.invalidateQueries();
                router.push('/auth/auth.page');
              }
            })
          );
        })
      );
    }
  }, [newOneToOneConversationRequest, operatorSocketClient, session, updateActivity, connected]);

  const onWebSocketClose = (evt: SyntheticEvent) => {
    setConnected(false);
    setLoading(false);
    if (operatorSocketClient) operatorSocketClient.reconnectDelay = 200 * Math.pow(2, currentTry);
    logger.info('Closed Event', evt);
  };

  const onWebSocketError = (frame: Frame) => {
    SetOperatorConnectionError(`Broker reported error: ${frame.headers.message}`);
    setConnected(false);
    logger.info(`Broker reported error: ${frame.headers.message}`);
  };

  const onWebSocketBeforeConnect = useCallback(() => {
    setCurrentTry((prevTry) => prevTry + 1);
  }, [currentTry]);

  useEffect(() => {
    if (currentTry > maxAttemptConnections) {
      setConnectionMsg(`Disconnecting after ${maxAttemptConnections} attempts`);
      setReTrying(false);
      operatorSocketClient?.deactivate();
    }
  }, [currentTry, operatorSocketClient]);

  const onWebSocketDisconnected = () => {
    setConnected(false);
    setLoading(false);
    logger.info(`Disconnected at ${new Date()}`);
  };

  //Connection : Connect Only once logged In
  useEffect(() => {
    if (!session.loggedIn || !operatorSocketClient) return;
    setLoading(true);
    operatorSocketClient.configure({
      brokerURL: `${process.env.NEXT_PUBLIC_WS_PLATFORM_URL}?test=${session.iamPrincipal?.security?.enable_feature_request || false}`,
      onWebSocketClose: onWebSocketClose,
      onStompError: onWebSocketError,
      onDisconnect: onWebSocketDisconnected,
      beforeConnect: onWebSocketBeforeConnect,
      reconnectDelay: 0,
      heartbeatIncoming: 15000,
      heartbeatOutgoing: 15000,
    });
  }, [session.loggedIn, operatorSocketClient, session?.iamPrincipal?.security?.enable_feature_request]);

  useEffect(() => {
    if (!operatorSocketClient && !operatorSocketClient.connected) return;
    if (!session.iamPrincipal) {
      return;
    }
    const interval = setInterval(() => {
      operatorSocketClient?.connected &&
        operatorSocketClient?.publish({
          destination: `/app/ping`,
          body: session.iamPrincipal.id,
        });
    }, 15000);
    return () => clearInterval(interval);
  }, [session.iamPrincipal]);

  useEffect(() => {
    if (!operatorSocketClient && !isOnline) return;
    operatorSocketClient.onConnect = onWebSocketConnected;
  }, [onWebSocketConnected, operatorSocketClient, isOnline]);

  useEffect(() => {
    if (!operatorSocketClient || !session.discoveryParams?.workspaceId || !isOnline) return;
    const getAuthUser = async () => await fetchAuthSession();
    getAuthUser()
      .then((succ) => {
        operatorSocketClient.connectHeaders = {
          idToken: succ.tokens?.idToken?.toString(),
          'x-rhs-workspace.id': session.discoveryParams?.workspaceId,
        };
        if (operatorSocketClient.state === ActivationState.INACTIVE) {
          operatorSocketClient.activate();
        }
      })
      .catch((err) => {
        mutateLogoutLogs({ tag: 'useSocket' });
        signOut();
        AbilityStore.clearRules();
        sessionStore.unsetDiscoveryParams();
        queryClient.invalidateQueries();
        router.push('/auth/auth.page');
      });
    return () => {
      if (operatorSocketClient.state === ActivationState.ACTIVE) {
        operatorSocketClient.deactivate();
      }
    };
  }, [operatorSocketClient, session.cognitoUser, session.discoveryParams?.workspaceId, isOnline, session.iamPrincipal?.security?.enable_feature_request]);

  return {
    client: operatorSocketClient,
    loading,
    error: operatorConnectionError,
    connected,
  };
};

export default useSocket;
